import React from 'react'
import {
  APPConfig,
  getCookie,
  getSessionStorage,
  getLocalStorage,
  setSessionStorage,
  deleteFromSessionStorage,
} from 'config/appConfig'
import fetchResponseFromAPI from './apiUtils'
import { mysiteContainer } from 'src/models/Mysite'
import { personalOfferContainer } from 'src/models/PersonalOffer'
import { getLocaleCodeFromUrl } from './localeUtils'
import {
  SPONSOR,
  STOREFRONT,
  CONST_DRAFT,
  MY_SITE_DRAFT,
  MYSITE_SPONSOR,
  CONST_UTM_INFO,
  CONST_PUBLISHED,
  NUSKIN_ACCOUNT_DATA,
  CONST_PERSONAL_OFFER,
  MY_SITE_SHOPPING_CONTEXT,
  CONST_IS_EQUINOX_MYSITE_OR_POSITE,
  PERSONAL_OFFER,
  MEMBER_PRICE,
  RETAIL_PRICE,
  PO_OFFER_STATUS_ACTIVE,
  BUNDLE_PRODUCT,
  KIT_PRODUCT,
  REGEX_TRAILING_EQUALS,
  REGEX_ABSOLUTE_URL,
  REGEX_STRIP_PROTOCOL,
  REGEX_STRIP_WWW,
  REGEX_DOMAIN_SPLIT,
  REGEX_MYSITE_PATH,
  REGEX_PERSONAL_OFFER_PATH,
  REGEX_WHITESPACE,
} from './constant'
import {
  trackErrorInInstana,
  setMysiteOrPersonalOffer,
  enableMysiteRedesignFeature,
  enablePORedesignFeature,
  enableSafeRedirectHardening,
  convertToBoolean,
  checkMysiteOrPersonalOffer,
  extractSegmentAfterPattern,
} from './commonUtils'
import {
  safeRedirect,
  safeAssign,
  isTrustedRedirectUrl,
  sanitizeSponsorId,
  sanitizePitchId,
  buildSameOriginPoPath,
  replaceMysitePathSegment,
} from './safeNavigate'
import { IS_BROWSER } from './application'
import ContentStack from 'src/api/contentStack'
import { customerContainer } from 'src/models'
import { pageNames } from 'src/routes/pathParams'

/* State management for mysite URL validation*/
let isMySiteUrlCache = null
let isMySiteUrlPromise = null
let cachedSubdomain = null
let mysiteShopHeaderCache = {}

function replaceLocation(url, fallbackUrl) {
  if (enableSafeRedirectHardening()) {
    safeRedirect(url, fallbackUrl)
  } else {
    window.location.replace(url)
  }
}

function assignLocation(url, fallbackUrl) {
  if (enableSafeRedirectHardening()) {
    safeAssign(url, fallbackUrl)
  } else {
    window.location.assign(url)
  }
}

function setLocationHref(url, fallbackUrl) {
  if (enableSafeRedirectHardening()) {
    safeRedirect(url, fallbackUrl)
  } else {
    window.location.href = url
  }
}

function mergeShareLinkQueryParams(shareLink, currentParams) {
  const shareLinkUrl = new URL(shareLink)
  currentParams.forEach((value, key) => {
    if (key !== 'pitchId') {
      if (value) {
        shareLinkUrl.searchParams.set(key, value)
      } else {
        shareLinkUrl.searchParams.set(key, '')
      }
    }
  })
  return shareLinkUrl.toString().replace(REGEX_TRAILING_EQUALS, '')
}

function getMysiteSponsor() {
  let mysiteSponsor
  if (mysiteContainer?.mysiteDomain) {
    mysiteSponsor = mysiteContainer.mysiteDomain
  } else if (mysiteContainer?.mysiteSponsorFromCustomer) {
    mysiteSponsor = mysiteContainer.mysiteSponsorFromCustomer
  } else {
    mysiteSponsor = getSessionStorage(MYSITE_SPONSOR)
  }
  return mysiteSponsor
}
/* Common function to determine if current request is for draft or published version */
function getMysiteContentVersion() {
  let isPublishedOrDraftVersion = CONST_PUBLISHED
  if (IS_BROWSER) {
    const urlParams = new URLSearchParams(window?.location?.search)
    isPublishedOrDraftVersion = urlParams.has(MY_SITE_DRAFT)
      ? CONST_DRAFT
      : CONST_PUBLISHED
  }
  return isPublishedOrDraftVersion
}

function getPoSponsorId() {
  const currentUrl = window.location.pathname
  const rawSponsorId =
    extractSegmentAfterPattern(currentUrl, '/personal-offer/') ||
    personalOfferContainer.poSiteSponsor ||
    getSessionStorage(SPONSOR)

  if (enableSafeRedirectHardening()) {
    return sanitizeSponsorId(rawSponsorId)
  }
  return rawSponsorId
}

/* Initialize mysite URL status early to cache the result*/
async function loadMySiteData() {
  deleteFromSessionStorage(MYSITE_SPONSOR)
  /* /signup URL does not contain a personal-offer path, so preserve
   * to allow nu-jitsu to reconstruct the PO site context. */
  if (!isSignupUrl()) {
    deleteFromSessionStorage(CONST_IS_EQUINOX_MYSITE_OR_POSITE)
  }
  if (IS_BROWSER && isEquinoxMysite()) {
    await getMySiteUrlStatus().catch(error => {
      trackErrorInInstana({
        errorReport: 'Error initializing mysite URL status',
        errorData: error,
      })
    })
    await getMySiteTranslationContent()
  }
  if (IS_BROWSER && isEquinoxPOSite()) {
    await loadRequiredAPIforPO().catch(error => {
      trackErrorInInstana({
        errorReport: 'Error loading Personal Offer data',
        errorData: error,
      })
    })
  }
}

async function loadRequiredAPIforPO() {
  const siteType = 'posite'
  const sponsorId = getPoSponsorId()
  // Toggle PO loading flag so `<PersonalOfferPDPLoader>` can render its
  // skeleton while waiting; reset in `finally` so it clears on errors too.
  personalOfferContainer.isPersonalOfferLoading = true
  // Snapshot whatever sponsor was already validated by a prior successful
  // shop-header call. If shop-header fails, only persist this attempt's URL
  // sponsor after the offer API confirms the PO itself is valid.
  let sponsorIdForSession = getSessionStorage(SPONSOR) || ''
  let isSponsorConfirmedByShopHeader = false
  try {
    const shopHeaderResponse = await getMysiteShopHeader({
      sponsorId: sponsorId,
      siteType,
    })

    if (shopHeaderResponse?.statusCode === 200) {
      personalOfferContainer.setPOSiteShopHeaderResponse(
        shopHeaderResponse.sponsor
      )
      personalOfferContainer.poSiteSponsor =
        shopHeaderResponse.sponsor?.user?.sapId || sponsorId
      personalOfferContainer.headerApiError = null
      // Shop header confirmed this sponsor — safe to persist.
      sponsorIdForSession = personalOfferContainer.poSiteSponsor
      isSponsorConfirmedByShopHeader = true
    } else {
      personalOfferContainer.headerApiError =
        shopHeaderResponse?.sponsorErrorCode || 'defaultSponsorNotFoundError'
    }

    const sponsorLookupResponse = await getSponsorLookup(sponsorId)
    personalOfferContainer.sponsorLookupResponse =
      sponsorLookupResponse?.body || null

    const personalOfferResponse = await getPersonalOfferData()

    // Only store valid PO data (has offerId or shareLink), not error responses
    const isValidPOResponse =
      personalOfferResponse?.offerId || personalOfferResponse?.shareLink

    if (isValidPOResponse) {
      personalOfferContainer.personalOfferData = personalOfferResponse
      const urlParams = new URLSearchParams(window.location.search)
      personalOfferContainer.savedPitchID = urlParams.get('pitchId')

      if (!isSponsorConfirmedByShopHeader) {
        sponsorIdForSession = sponsorId
      }

      // For PO V2, store the entire offer data; for V1, store only shareLink
      if (enablePORedesignFeature()) {
        // New PO 2.0 structure with offerId, shareLinks, products, messages, etc.
        setSessionStorage('personalOffer', personalOfferResponse)
        const offerPriceType = personalOfferResponse?.priceType || ''
        personalOfferContainer.poPriceType = offerPriceType?.toLowerCase() || ''

        // Validate locale against marketSettings and redirect if needed
        validateAndRedirectPOLocale(personalOfferResponse, sponsorId)
      } else {
        const shareLink = personalOfferResponse?.shareLink || {}
        setSessionStorage('personalOffer', { shareLink })
      }
    }

    // Persist offer API failure in sessionStorage so it survives Okta redirect
    // Offer API receives sponsorId, so if sponsor is invalid it fails automatically
    if (!isValidPOResponse) {
      setSessionStorage('poSiteOfferApiFailed', true)
    } else {
      deleteFromSessionStorage('poSiteOfferApiFailed')
    }

    // Persist the shopping context now that poPriceType (if any) is known.
    // If shop-header 404s but the offer API succeeds, keep the PO context by
    // persisting the URL sponsor; when both fail, do not persist the candidate.
    if (sponsorIdForSession) {
      setSessionStorageData(sponsorIdForSession, siteType)
    } else {
      deleteFromSessionStorage(SPONSOR)
    }

    return personalOfferResponse
  } finally {
    personalOfferContainer.isPersonalOfferLoading = false
  }
}

function validateAndRedirectPOLocale(offerData, sponsorId) {
  if (!IS_BROWSER || !offerData) return

  const marketSettings = offerData?.marketSettings || []
  const defaultLanguage = offerData?.messages?.defaultLanguage || 'en'
  const shareLinks = offerData?.shareLinks || {}

  if (marketSettings.length === 0) return

  const hardened = enableSafeRedirectHardening()
  const safeSponsorId = hardened ? sanitizeSponsorId(sponsorId) : sponsorId
  if (hardened && !safeSponsorId) return

  const pathParts = window.location.pathname?.split('/')?.filter(Boolean) || []
  const currentMarketFromUrl = pathParts[0] || ''
  const currentLangFromUrl = pathParts[1] || ''

  const currentMarketUpper = currentMarketFromUrl?.toUpperCase()
  const currentLangLower = currentLangFromUrl?.toLowerCase()

  const offerMarket = marketSettings[0]
  const offerMarketCode = offerMarket?.market?.toUpperCase() || ''
  const availableLanguages = offerMarket?.languages || []

  const currentParams = new URLSearchParams(window.location.search)
  const pitchIdRaw = offerData?.offerId || getPersonalOfferPitchId()
  const pitchId = hardened ? sanitizePitchId(pitchIdRaw) : pitchIdRaw
  currentParams.set('pitchId', pitchId)
  const queryString = currentParams
    .toString()
    .replace(REGEX_TRAILING_EQUALS, '')

  const resolvedSponsorId = safeSponsorId || sponsorId

  const buildFallbackRedirectUrl = fallbackLang => {
    const redirectPath = `/${offerMarketCode.toLowerCase()}/${fallbackLang}/personal-offer/${resolvedSponsorId}?${queryString}`
    return `${window.location.origin}${redirectPath}`
  }

  if (currentMarketUpper === offerMarketCode) {
    const isLanguageValid = availableLanguages.some(
      lang => lang?.toLowerCase() === currentLangLower
    )

    if (!isLanguageValid) {
      const fallbackLang = availableLanguages.includes(defaultLanguage)
        ? defaultLanguage
        : availableLanguages[0] || 'en'
      replaceLocation(buildFallbackRedirectUrl(fallbackLang))
    }
  } else {
    const fallbackLang = availableLanguages.includes(defaultLanguage)
      ? defaultLanguage
      : availableLanguages[0] || 'en'
    const shareLink = shareLinks[offerMarketCode]
    if (shareLink) {
      const cleanUrl = mergeShareLinkQueryParams(shareLink, currentParams)
      if (hardened && !isTrustedRedirectUrl(cleanUrl)) {
        const fallbackPath = buildSameOriginPoPath({
          market: offerMarketCode.toLowerCase(),
          language: fallbackLang,
          sponsorId: resolvedSponsorId,
          queryString,
        })
        replaceLocation(
          fallbackPath ? `${window.location.origin}${fallbackPath}` : '/',
          '/'
        )
      } else {
        replaceLocation(cleanUrl, buildFallbackRedirectUrl(fallbackLang))
      }
    } else {
      replaceLocation(buildFallbackRedirectUrl(fallbackLang))
    }
  }
}

async function fetchAndValidateSubdomain() {
  const subdomainName = getSubdomainName()
  const locale = getLocaleCodeFromUrl()?.split('_')?.[1] || 'US'

  const isPublishedOrDraftVersion = getMysiteContentVersion()
  let param = {
    endPointName: 'mysiteSubDomainValidation',
    credentials: 'include',
    isToSendStoreId: false,
    isToRemoveCommonHeaders: ['locale', 'x-store-id'],
    isToSendLocaleInQueryParam: false,
    pathParams: `${subdomainName}?market=${locale}`,
    headers: {
      Client_id: APPConfig.getAppConfig()?.client_id || '',
      IsMysiteSponsorCookieSet: true,
    },
  }
  if (isPublishedOrDraftVersion === 'draft') {
    param.credentials = 'omit'
    delete param.headers.IsMysiteSponsorCookieSet
  }
  let response = null
  try {
    response = await fetchResponseFromAPI(param)
    if (response?.statusCode === 200) {
      return response
    } else {
      mysiteContainer.setMysiteApiError(
        response?.sponsorErrorCode || 'defaultSponsorNotFoundError'
      )
    }
  } catch (error) {
    console.log('Error fetching subdomain:', error)
    mysiteContainer.setMysiteApiError('defaultSponsorNotFoundError')
    trackErrorInInstana({
      errorData: {
        payload: param,
        apiResponse: response,
        subdomain: subdomainName,
      },
      errorReport: 'Error fetching mysite subdomain',
    })
    return null
  }
}

/* Check if URL contains /mysite/ as a complete path */
async function checkValidMySiteUrl() {
  const urlSubdomainName = getSubdomainName()

  let result = false
  if (isEquinoxMysite() && urlSubdomainName) {
    let response = await fetchAndValidateSubdomain()
    if (response?.statusCode === 200) {
      mysiteContainer.invalidMysiteDomain = false
      let subDomain = urlSubdomainName || ''
      let sponsorId = response?.sponsor || ''
      /*If url contains sponsor id/sap id then we are triggering getMysiteShopHeader api to check sponsor id valid and have the details*/
      if (response?.sponsor === null) {
        response = await getMysiteShopHeader({ sponsorId: urlSubdomainName })
        subDomain = response?.sponsor?.user?.shopUrl || ''
        sponsorId = response?.sponsor?.user?.sapId || ''
      } else {
        /* Fetch and store mysite shop header details */
        sponsorId = response?.sponsor || ''
        if (response?.sponsor !== null) {
          await getMysiteShopHeader({ sponsorId })
        }
      }
      /*seting the value of subdomain in session storage */
      mysiteContainer.mysiteDomain = subDomain
      setSessionStorage(MYSITE_SPONSOR, subDomain)

      /*if url contains sponsor id or sapId then we are replacing the url with subdomain*/
      if (IS_BROWSER) {
        const { href, pathname, search, hash } = window.location
        if (sponsorId && href?.includes(sponsorId)) {
          if (enableSafeRedirectHardening()) {
            const newPathname = replaceMysitePathSegment({
              pathname,
              oldSegment: sponsorId,
              newSegment: subDomain,
            })
            if (newPathname) {
              replaceLocation(
                `${window.location.origin}${newPathname}${search}${hash}`
              )
            }
          } else {
            const newUrl = href?.replace(sponsorId, subDomain)
            window.location.replace(newUrl)
          }
        }
      }
      result = true
    } else {
      mysiteContainer.invalidMysiteDomain = true
      /* deleteing all mysite related data if subdomain is invalid */
      deleteMysiteRelatedData()
    }
  }
  return result
}

/* Get mysite URL validation status with caching */
async function getMySiteUrlStatus(options) {
  const { sync = false } = options || {}
  if (sync && !isMySiteUrlCache) {
    return isMySiteUrlCache
  }
  /* Get current subdomain to check if it changed*/
  const currentSubdomain = getSubdomainName()

  /* If subdomain changed, clear cache and re-validate*/
  if (cachedSubdomain !== null && cachedSubdomain !== currentSubdomain) {
    isMySiteUrlCache = null
    isMySiteUrlPromise = null
    cachedSubdomain = null
  }

  // Always return cached result if available (no need to re-validate)
  if (isMySiteUrlCache !== null) {
    return Promise.resolve(isMySiteUrlCache)
  }

  // Return existing promise if validation is in progress
  if (isMySiteUrlPromise) {
    return isMySiteUrlPromise
  }

  /* Start new validation only if no cache and no ongoing promise*/
  isMySiteUrlPromise = await checkValidMySiteUrl()
    .then(result => {
      isMySiteUrlCache = result
      isMySiteUrlPromise = null
      cachedSubdomain = currentSubdomain
      return result
    })
    .catch(error => {
      console.log('Error validating mysite URL:', error)
      isMySiteUrlPromise = null
      deleteMysiteRelatedData()
      /*Return false on error but don't cache it (allow retry)*/
      return false
    })
  return isMySiteUrlPromise
}
/* get mysite shop header data */
async function getMysiteShopHeader(props) {
  const { sponsorId, siteType = 'mysite' } = props || {}
  const isPublishedOrDraftVersion = getMysiteContentVersion()
  const cacheKey = `${sponsorId}_${isPublishedOrDraftVersion}`

  if (mysiteShopHeaderCache[cacheKey]) {
    return mysiteShopHeaderCache[cacheKey]
  }
  const locale = getLocaleCodeFromUrl()?.split('_')?.[1] || 'US'
  /* if we need draft version data then we need to pass auth token in header **/
  const authToken = getLocalStorage(NUSKIN_ACCOUNT_DATA)?.user?.eid

  let params = {
    endPointName: 'mysiteSponsorData',
    credentials: 'include',
    isToSendStoreId: false,
    isToSendLocaleInQueryParam: false,
    isToRemoveCommonHeaders: ['x-store-id', 'locale'],
    pathParams: `${isPublishedOrDraftVersion}/sponsor/${sponsorId}?market=${locale}`,
    headers: {
      Client_id: APPConfig.getActiveAppConfig()?.client_id || '',
      IsMysiteSponsorCookieSet: true,
    },
  }

  if (isPublishedOrDraftVersion === 'draft') {
    params.headers.Authorization = `JWT ${authToken}`
    params.credentials = 'omit'
    params.pathParams = `${isPublishedOrDraftVersion}/sponsor?market=${locale}`
    delete params.headers.IsMysiteSponsorCookieSet
  }
  let result = null
  try {
    result = await fetchResponseFromAPI(params)
    if (result?.statusCode === 200) {
      /*seting the sponsor data in session storage */
      setSessionStorage(STOREFRONT, result?.sponsor)
      mysiteContainer.setMysiteShopHeaderResponse(result?.sponsor)
      const sapId =
        result?.sponsor?.user?.sapId ||
        result?.sponsor?.user?.userId ||
        sponsorId
      mysiteContainer.mysiteSponsor = sapId
      mysiteShopHeaderCache[cacheKey] = result?.sponsor
      if (siteType === 'mysite') {
        setSessionStorageData(sapId, siteType)
      }
      return result
    } else {
      mysiteContainer.setMysiteApiError(
        result?.sponsorErrorCode || 'defaultSponsorNotFoundError'
      )
      deleteMysiteRelatedData()
      trackErrorInInstana({
        errorData: {
          payload: params,
          apiResponse: result,
          sponsorId: sponsorId,
        },
        errorReport: 'Invalid sponsor or invalid domain.',
      })
    }
  } catch (error) {
    mysiteContainer.setMysiteApiError('defaultSponsorNotFoundError')
    console.log(
      `Failed to fetch mysite shop related data for the sponsor id`,
      error
    )
    trackErrorInInstana({
      errorData: {
        payload: params,
        apiResponse: result,
        sponsorId: sponsorId,
      },
      errorReport: `Failed to fetch mysite shop related data for the sponsor id ${sponsorId}`,
    })
    return result
  }
}

/* get sponsor lookup data – validates BA (e.g. detects downgraded sponsors).
 * Returns the raw API response: { body: { valid, errorCode?, ... }, statusCode }. */
async function getSponsorLookup(sponsorIdArg) {
  const sponsorId = sponsorIdArg || getPoSponsorId()
  if (!sponsorId) return null

  const country = getLocaleCodeFromUrl()?.split('_')?.[1]?.toUpperCase()
  if (!country) return null

  const params = {
    endPointName: 'sponsorLookup',
    credentials: 'omit',
    isToSendStoreId: false,
    isToSendLocaleInQueryParam: false,
    isToIncludeCommomHeaders: false,
    deleteContentType: true,
    pathParams: `${country}/${sponsorId.toUpperCase()}`,
  }

  try {
    return await fetchResponseFromAPI(params)
  } catch (error) {
    console.log('getSponsorLookup: failed', error)
    return null
  }
}

/* get personal offer data */
async function getPersonalOfferData(props) {
  const { offerId } = props || {}

  const pitchId = getPersonalOfferPitchId()
  const sponsorId = getPoSponsorId()
  const isProductOfferV2Enabled = enablePORedesignFeature()

  if (!sponsorId || !pitchId) {
    console.log('getPersonalOfferData: Missing sponsorId or pitchId')
    return null
  }

  let params = {
    endPointName: isProductOfferV2Enabled
      ? 'personalOfferDataV2'
      : 'personalOfferData',
    credentials: 'omit',
    isToSendStoreId: false,
    isToSendLocaleInQueryParam: false,
    isToRemoveCommonHeaders: ['x-store-id', 'locale'],
    pathParams: isProductOfferV2Enabled
      ? `${pitchId}`
      : `${sponsorId}/share-link/${pitchId}`,
    headers: {
      Client_id: APPConfig.getActiveAppConfig()?.client_id || '',
      ...(isProductOfferV2Enabled && { sponsorId: sponsorId || '' }),
    },
  }

  try {
    const result = await fetchResponseFromAPI(params)
    return result
  } catch (error) {
    console.log('Failed to fetch personal offer data', error)
    trackErrorInInstana({
      errorData: {
        payload: params,
        apiResponse: null,
        offerId: offerId,
        sponsorId: sponsorId,
        pitchId: pitchId,
      },
      errorReport: `Failed to fetch personal offer data for offer id ${offerId}`,
    })
    return null
  }
}
function setShopingContext(siteType) {
  let showWholeSalePricing = false
  if (siteType === 'mysite') {
    const sponsorData = mysiteContainer?.mysiteShopHeaderResponse
    const language = getLocaleCodeFromUrl()?.split('_')?.[0] || 'en'
    const isMysiteMemberPrice =
      sponsorData?.market?.lang?.[language]?.priceType?.toLowerCase() ===
        'member' || sponsorData?.market?.lang?.[language]?.wholesale

    mysiteContainer.isMysiteMemberPrice = isMysiteMemberPrice

    showWholeSalePricing = isMysiteMemberPrice
  } else if (siteType === 'posite') {
    if (enablePORedesignFeature()) {
      const poPriceType = personalOfferContainer?.poPriceType || ''
      showWholeSalePricing = poPriceType === MEMBER_PRICE
    } else {
      showWholeSalePricing =
        personalOfferContainer?.personalOfferData?.shareLink
          ?.useMemberPricing || false
    }
  }
  let contextOptions = {
    showWholeSalePricing,
  }

  if (showWholeSalePricing) {
    contextOptions.allowAnonymousShopping = false
    contextOptions.preferredSignupOnly = true
  } else {
    contextOptions.allowAnonymousShopping = true
    contextOptions.preferredSignupOnly = false
  }

  const shoppingContextValue = {
    context: siteType === 'posite' ? PERSONAL_OFFER : STOREFRONT,
    overrides: contextOptions,
  }
  setSessionStorage(MY_SITE_SHOPPING_CONTEXT, shoppingContextValue)
}

function getSubdomainName() {
  let subdomainName = null
  if (IS_BROWSER) {
    const currentUrl = window.location.pathname
    subdomainName = extractSegmentAfterPattern(currentUrl, '/mysite/')
  }
  return subdomainName
}
function isEquinoxMysiteUrl() {
  let isEqMysiteUrl = false
  if (IS_BROWSER && window?.location?.pathname) {
    const currentUrl = window.location.pathname
    isEqMysiteUrl = currentUrl.includes('/mysite/')
  }
  return isEqMysiteUrl
}

function isEquinoxPOUrl() {
  let isEqMysiteUrl = false
  if (IS_BROWSER && window?.location?.pathname) {
    const currentUrl = window.location.pathname
    isEqMysiteUrl = currentUrl.includes('/personal-offer/')
  }
  return isEqMysiteUrl
}

function isSignupUrl() {
  if (IS_BROWSER && window?.location?.pathname) {
    return window.location.pathname.includes('/signup')
  }
  return false
}

function isEquinoxMysite() {
  const isEqMysite = isEquinoxMysiteUrl() && enableMysiteRedesignFeature()
  return isEqMysite || false
}
function isEquinoxPOSite() {
  const isEqMysite = isEquinoxPOUrl() && enablePORedesignFeature()
  return isEqMysite || false
}

function isPOSiteInvalid() {
  if (!IS_BROWSER) {
    return { invalid: false, reason: '' }
  }
  if (!enablePORedesignFeature()) {
    return { invalid: false, reason: '' }
  }

  // Only fire on the PO landing route itself — never on nested PDP routes
  // like `/personal-offer/{sponsor}/product/{slug}`. A PDP opened in a new
  // tab from PO does not fetch `personalOfferData`, so a pure `.includes`
  // check would falsely report the offer as invalid and cause a NoOfferHere
  // UI to appear inside the Buy Now / Express Checkout drawer.
  const pathname =
    typeof window?.location?.pathname === 'string'
      ? window.location.pathname
      : ''
  const isPoUrl =
    pathname.includes('/personal-offer/') && !pathname.includes('/product/')
  if (!isPoUrl) {
    return { invalid: false, reason: '' }
  }

  if (personalOfferContainer?.isPersonalOfferLoading) {
    return { invalid: false, reason: '' }
  }

  const offer = personalOfferContainer?.personalOfferData
  const offerSucceeded = !!offer && (!!offer.offerId || !!offer.shareLink)
  if (!offerSucceeded) {
    return { invalid: true, reason: 'invalid_offer' }
  }
  const responseStatus = offer?.responseStatus
  if (responseStatus && responseStatus !== PO_OFFER_STATUS_ACTIVE) {
    return { invalid: true, reason: 'invalid_offer' }
  }
  return { invalid: false, reason: '' }
}

function isEquinoxMysiteAdmin() {
  return IS_BROWSER && window.location.pathname
    ? window.location.pathname?.includes('mysite-admin')
    : false
}

function isEquinoxPOAdmin() {
  return IS_BROWSER && window.location.pathname
    ? window.location.pathname?.includes(pageNames.productOfferAdmin) &&
        enablePORedesignFeature()
    : false
}

function isEquinoxMysiteAdminPreview() {
  if (typeof window === 'undefined') return false
  const urlParams = new URLSearchParams(window.location.search)
  const isMysitePreview =
    isEquinoxMysiteUrl() &&
    enableMysiteRedesignFeature() &&
    urlParams.has('preview')
  return isMysitePreview || false
}

/*check if current page is mysite home page */
function isMysiteHomePage() {
  let isMysiteHomePage = false
  const currentLocale = getLocaleCodeFromUrl()?.toLowerCase()
  const curLocaleFromUrl = currentLocale?.split('_')?.reverse()?.join('/')

  if (curLocaleFromUrl && isEquinoxMysite()) {
    const mysiteSponsor = getMysiteSponsor()

    const mysiteUrl = `/${curLocaleFromUrl}/mysite/${mysiteSponsor}`
    isMysiteHomePage = location.pathname?.endsWith('/')
      ? mysiteUrl + '/' === location.pathname
      : location.pathname === mysiteUrl
  }
  return isMysiteHomePage
}

/* Check if current page is PO (Personal Offer) home page */
function isPOHomePage() {
  const currentLocale = getLocaleCodeFromUrl()?.toLowerCase()
  const curLocaleFromUrl = currentLocale?.split('_')?.reverse()?.join('/')

  if (!curLocaleFromUrl || !isEquinoxPOSite()) {
    return false
  }

  const poSponsor = getPoSponsorId()
  const poUrl = `/${curLocaleFromUrl}/personal-offer/${poSponsor}`

  return location.pathname?.endsWith('/')
    ? poUrl + '/' === location.pathname
    : location.pathname === poUrl
}

/* Check if current page is mysite or PO home page */
function isMysiteOrPOHomePage() {
  return isMysiteHomePage() || isPOHomePage()
}
function getImageUrl(data) {
  if (!data) return null
  /* Check if data already includes a protocol (absolute URL) */
  if (REGEX_ABSOLUTE_URL.test(data)) {
    return data
  }
  const envDomain =
    APPConfig.getActiveAppConfig()?.localeResourceFilesDomain || ''
  return `https://${envDomain}${data}`
}
/* deleteing all mysite related data if subdomain is invalid */
function deleteMysiteRelatedData() {
  /* if current url subdomain and saved subdaomin in cookie are same then only we are deleting mysite related data */
  const isExistedMysiteCookie =
    getSubdomainName() === getMysiteSponsor() || false
  if (enableMysiteRedesignFeature() && isExistedMysiteCookie) {
    clearMysiteSessionStorage()
  }
}
const clearMysiteSessionStorage = () => {
  deleteFromSessionStorage(SPONSOR)
  deleteFromSessionStorage(STOREFRONT)
  deleteFromSessionStorage(MY_SITE_SHOPPING_CONTEXT)
  deleteFromSessionStorage(CONST_IS_EQUINOX_MYSITE_OR_POSITE)
  deleteFromSessionStorage('poSiteOfferApiFailed')
  window.shoppingContextJson = {}
}

function getSocialMediaType(url) {
  if (!url) return 'unknown'
  const lowerUrl = url.toLowerCase()
  /* Remove http:// or https:// */
  let domain = lowerUrl.replace(REGEX_STRIP_PROTOCOL, '')
  /* Remove www. prefix if present */
  domain = domain.replace(REGEX_STRIP_WWW, '')
  /* Extract the main domain name (before first dot or slash)*/
  const domainName = domain.split(REGEX_DOMAIN_SPLIT)[0]
  return domainName || 'unknown'
}
function checkBASponsorIdMismatch(overrideSponsorId) {
  if (IS_BROWSER && isEquinoxPOSite()) {
    const urlParams = new URLSearchParams(window.location.search || '')
    if (urlParams.has('preview')) {
      return false
    }
  }
  const { accountType = '', referenceAttribute = '' } =
    customerContainer.profileResponse?.accounts || {}

  const isLoggedIn = customerContainer.isRegisterUser || false
  const isBrandAffiliate =
    customerContainer.isBrandAffiliate?.(accountType) || false
  const baqProperty =
    customerContainer?.profileResponse?.accounts?.properties?.find(
      element => element.attributeId === 'BAQ'
    )
  const isBAQEnabled = baqProperty ? convertToBoolean(baqProperty.value) : true
  const customerProfileId = referenceAttribute || ''

  // On PO sites, skip mismatch redirect only when offer API failed (invalid sponsor)
  // If offer is valid, BA-to-BA mismatch redirect should still work
  const poSiteOfferApiFailed = getSessionStorage('poSiteOfferApiFailed')
  if (
    !overrideSponsorId &&
    (poSiteOfferApiFailed ||
      (isEquinoxPOSite() && !personalOfferContainer?.personalOfferData))
  ) {
    return false
  }

  const urlSponsorId =
    overrideSponsorId ||
    (isEquinoxPOSite()
      ? personalOfferContainer?.poSiteSponsor || getPoSponsorId()
      : mysiteContainer.mysiteSponsor)

  // For PO sites, redirect any BA (including the owner) away from the PO page
  if (isEquinoxPOSite()) {
    return isLoggedIn && isBrandAffiliate && isBAQEnabled
  }

  return (
    isLoggedIn &&
    isBrandAffiliate &&
    isBAQEnabled &&
    customerProfileId !== urlSponsorId
  )
}
function getBaseName(baseName) {
  const currentUrl = window?.location?.href
  const mysiteSponsor = getMysiteSponsor()
  const skipMysiteRedirection =
    APPConfig.getActiveAppConfig()?.skipMysiteRedirection || []
  const poSponsorId = getPoSponsorId()
  const isBAMismatch = checkBASponsorIdMismatch()

  if (mysiteContainer.invalidMysiteDomain) {
    return baseName
  } else if (
    isEquinoxMysite() &&
    mysiteSponsor &&
    !skipMysiteRedirection?.includes(currentUrl)
  ) {
    /*if the login BA Sponsor Id mismatch with mysite sponsor id then we are redirecting user to non-mysite */
    if (isBAMismatch) {
      redirectToNonMysite()
    }
    return `${baseName}/mysite/${mysiteSponsor}`
  } else if (poSponsorId && isEquinoxPOSite()) {
    if (isBAMismatch) {
      redirectToNonPOsite()
    }
    return `${baseName}/personal-offer/${poSponsorId}`
  } else if (
    /* If shoppingContext and context === 'personal_offer' or
     * utmInfo exists in storage or in url, just return baseName*/
    (getLocalStorage(MY_SITE_SHOPPING_CONTEXT) &&
      getLocalStorage(MY_SITE_SHOPPING_CONTEXT)?.context ===
        CONST_PERSONAL_OFFER) ||
    getSessionStorage(CONST_UTM_INFO) ||
    getCookie(CONST_UTM_INFO) ||
    currentUrl?.includes('utm_') ||
    isEquinoxPOSite()
  ) {
    return baseName
  } else if (
    /* If mysiteSponsor exists and currentUrl does NOT include in skipMysiteRedirection'
     * AND we haven't detected a sponsor mismatch */
    enableMysiteRedesignFeature() &&
    mysiteSponsor &&
    !skipMysiteRedirection.some(path => currentUrl?.includes(path)) &&
    !isBAMismatch
  ) {
    redirectToMysite(currentUrl, baseName, mysiteSponsor)
  }
  return baseName
}

const redirectToNonMysite = () => {
  clearMysiteSessionStorage()
  const nonMysiteSponsorPath = window.location.pathname.replace(
    REGEX_MYSITE_PATH,
    ''
  )
  return assignLocation(nonMysiteSponsorPath)
}
const redirectToNonPOsite = () => {
  clearMysiteSessionStorage()
  const nonPOsiteSponsorPath = window.location.pathname.replace(
    REGEX_PERSONAL_OFFER_PATH,
    ''
  )
  return assignLocation(nonPOsiteSponsorPath)
}

const redirectToMysite = (currentUrl, baseName, mysiteSponsor) => {
  const path = currentUrl?.split(`/${baseName}/`)[1] || ''
  const newUrl = '/' + baseName + '/mysite/' + mysiteSponsor + '/' + path
  if (enableSafeRedirectHardening()) {
    assignLocation(newUrl)
    return
  }
  try {
    const fullUrl = new URL(newUrl, window?.location?.origin)
    if (
      fullUrl?.origin?.includes('nuskin.com') &&
      fullUrl?.origin === window.location.origin
    ) {
      window.location.assign(newUrl)
    } else {
      console.error('Invalid redirect URL: different origin')
    }
  } catch (e) {
    console.error('Invalid URL construction:', e)
  }
}

async function getMySiteTranslationContent() {
  const locale =
    getLocaleCodeFromUrl()?.split('_')?.reverse()?.join('-') || 'US-en'
  const csData = await ContentStack.getEntry('nextgen_mysite', locale)
  const result = { ...csData }

  const defaultInfo = {
    categoryName:
      result?.mysite_admin_categories_products_screen?.categories_products
        ?.default_category_name ?? '',
    categoryDescription:
      result?.mysite_admin_categories_products_screen?.categories_products
        ?.default_category_description ?? '',
    siteDescription:
      result?.mysite_admin_banner_screen?.banner?.profile_photo
        ?.site_description ?? '',
  }
  mysiteContainer.setMysiteDefaultCategoryInfo(defaultInfo)
}
const getPersonalOfferPitchId = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const urlPitchId = urlParams.get('pitchId') || ''
  const personalOfferData = getSessionStorage('personalOffer') || {}
  const pitchId =
    urlPitchId ||
    personalOfferContainer.savedPitchID ||
    personalOfferData?.offerId ||
    personalOfferData?.shareLink?.id ||
    ''
  if (enableSafeRedirectHardening()) {
    return sanitizePitchId(pitchId)
  }
  return pitchId
}

/**
 * Resolves the redirect URL used when the header logo is clicked on a
 * Personal Offer (PO) site. Always reconstructs the PO landing URL from
 * the locale segment of the current pathname + resolved sponsor + pitch
 * so the user lands on the original PO entry point (with its `pitchId`),
 * even when invoked from pages like `/viewcart` or `/checkout`.
 *
 * Resolution:
 *   - basePath: locale prefix taken from the segment of
 *     `window.location.pathname` before `/personal-offer/`. Because this
 *     helper is only invoked from PO sites (gated by `isEquinoxPOSite()`),
 *     the current URL is always under `/personal-offer/`, even on nested
 *     routes like `/in/en/personal-offer/<sponsor>/product/<sku>` or
 *     `/us/en/personal-offer/<sponsor>/viewCart`. Deriving from the live
 *     URL preserves the locale prefix which `prevPageUrl` from the route
 *     tracker does not include.
 *   - sponsorId: `getPoSponsorId()` (URL → containers → session).
 *   - pitchId: `getPersonalOfferPitchId()` with a best-effort fallback
 *     to parsing `?pitchId=` from `prevPageUrl` in local storage.
 *
 * @returns {string|null} Absolute URL to redirect to, or null when no
 *   sponsor can be resolved (caller should perform default navigation).
 */
const getPersonalOfferRedirectUrl = () => {
  if (!IS_BROWSER) {
    return null
  }
  const sponsorId = getPoSponsorId()
  if (!sponsorId) {
    return null
  }

  let pitchId = getPersonalOfferPitchId()
  const prevPageUrl = getLocalStorage('prevPageUrl') || ''
  if (!pitchId && prevPageUrl) {
    try {
      const parsed = new URL(prevPageUrl, window.location.origin)
      pitchId = parsed.searchParams.get('pitchId') || ''
    } catch (err) {
      console.error('Error parsing prevPageUrl for pitchId', err)
    }
  }

  const currentPath = window?.location?.pathname || ''
  const basePath = currentPath.includes('/personal-offer/')
    ? currentPath.split('/personal-offer/')[0]
    : ''
  const pitchQuery = pitchId
    ? `?pitchId=${enableSafeRedirectHardening() ? encodeURIComponent(sanitizePitchId(pitchId)) : pitchId}`
    : ''
  return `${window.location.origin}${basePath}/personal-offer/${sponsorId}${pitchQuery}`
}

const getLocalePartsFromUrl = () => {
  const curLocale = getLocaleCodeFromUrl({ isReverseType: true }) || ''
  if (!curLocale) return null
  const parts = curLocale.includes('_')
    ? curLocale.split('_')
    : curLocale.split('-')
  const [language, country] = parts
  if (!language || !country) return null
  return { language, country }
}

const redirectToLocaleHome = () => {
  if (!IS_BROWSER) return
  const localeParts = getLocalePartsFromUrl()
  if (!localeParts) return
  const { language, country } = localeParts
  setLocationHref(
    `${window.location.origin}/${country}/${language}`.toLowerCase()
  )
}

const redirectToSignIn = () => {
  if (!IS_BROWSER) return
  const pathSegments = window.location.pathname.split('/').filter(Boolean)
  const country = pathSegments[0]
  const language = pathSegments[1]
  setLocationHref(
    `${window.location.origin}/${country}/${language}/signin`.toLowerCase()
  )
}

const redirectToPOLanding = () => {
  if (!IS_BROWSER) return
  const sponsorId = getPoSponsorId() || getMysiteSponsor()
  if (!sponsorId) {
    redirectToLocaleHome()
    return
  }
  const localeParts = getLocalePartsFromUrl()
  if (!localeParts) return
  const { language, country } = localeParts
  const localePrefix = `/${country}/${language}`.toLowerCase()
  if (enablePORedesignFeature()) {
    const pitchId = getPersonalOfferPitchId()
    const pitchIdParam = pitchId ? `?pitchId=${pitchId}` : ''
    setLocationHref(
      `${window.location.origin}${localePrefix}/personal-offer/${sponsorId}${pitchIdParam}`
    )
  } else {
    setLocationHref(
      `${window.location.origin}${localePrefix}/mysite/${sponsorId}`
    )
  }
}

function getGTMMemberPricing(productData = {}) {
  const wholeSalePriceNode =
    productData?.totalValue?.priceFacets?.['Wholesale Price']
  if (!wholeSalePriceNode) return null

  const wholeSalePrice = wholeSalePriceNode?.['Wholesale Price']
  const wholeSalePriceAfterDiscountNode =
    wholeSalePriceNode?.['Wholesale PriceAfterDiscount']

  if (wholeSalePriceNode) {
    if (wholeSalePriceAfterDiscountNode) {
      if (
        wholeSalePrice === wholeSalePriceAfterDiscountNode ||
        wholeSalePriceAfterDiscountNode > wholeSalePrice
      ) {
        return {
          originalPrice: wholeSalePriceAfterDiscountNode,
          priceAfterDiscount: wholeSalePriceAfterDiscountNode,
        }
      } else {
        return {
          originalPrice: wholeSalePrice,
          priceAfterDiscount: wholeSalePriceAfterDiscountNode,
        }
      }
    } else {
      return {
        originalPrice: wholeSalePriceNode?.['Wholesale Price'],
        priceAfterDiscount: wholeSalePriceNode?.['Wholesale Price'],
      }
    }
  }
}

function getGTMRetailPricing(productData = {}) {
  const retailPriceNode =
    productData?.totalValue?.priceFacets?.['Regular Price']
  if (!retailPriceNode) return null

  const retailPrice = retailPriceNode?.['Regular Price']
  const retailPriceAfterDiscountNode =
    retailPriceNode?.['Regular PriceAfterDiscount']

  if (retailPriceNode) {
    if (retailPriceAfterDiscountNode) {
      if (
        retailPrice === retailPriceAfterDiscountNode ||
        retailPriceAfterDiscountNode > retailPrice
      ) {
        return {
          originalPrice: retailPriceAfterDiscountNode,
          priceAfterDiscount: retailPriceAfterDiscountNode,
        }
      } else {
        return {
          originalPrice: retailPrice,
          priceAfterDiscount: retailPriceAfterDiscountNode,
        }
      }
    } else {
      return {
        originalPrice: retailPriceNode?.['Regular Price'],
        priceAfterDiscount: retailPriceNode?.['Regular Price'],
      }
    }
  }
}

function getGTMEquinoxMysitePrice(unitPrice, productData) {
  const priceType = mysiteContainer?.mysitePriceType || ''
  if (!customerContainer?.isRegisterUser && isEquinoxMysite()) {
    if (mysiteContainer?.mysitePriceType === MEMBER_PRICE) {
      return getGTMMemberPricing(productData) || unitPrice
    }
    return unitPrice
  } else if (customerContainer?.isRegisterUser) {
    if (priceType?.toLowerCase() == MEMBER_PRICE) {
      return getGTMMemberPricing(productData)
    } else if (priceType?.toLowerCase() == RETAIL_PRICE) {
      return getGTMRetailPricing(productData)
    }
  }

  return unitPrice
}

function getProductPriceForTracking(product = {}, productType = '') {
  let unitPrice = {}

  if (productType === BUNDLE_PRODUCT || productType === KIT_PRODUCT) {
    unitPrice = product?.totalValue || product?.prices?.[0] || {}
    return getGTMEquinoxMysitePrice(unitPrice, product)
  }

  unitPrice =
    product?.sku?.[0]?.totalValue || product?.sku?.[0]?.prices?.[0] || {}
  const productSku = product?.sku?.[0] || {}
  return getGTMEquinoxMysitePrice(unitPrice, productSku)
}
function findDefaultSku(options) {
  const { sku } = options || {}
  return sku?.find(sku => sku?.default == true)
}
/**
 * Constructs a personal offer URL with sponsor ID, locale, and pitch ID
 * @returns {string|null} The constructed personal offer URL or null if error occurs
 */
function buildPersonalOfferUrl(calledFrom = '') {
  const currentPath = window?.location?.pathname || ''
  const hasMysite =
    personalOfferContainer?.poSiteShopHeaderResponse?.user?.userId || ''
  if (
    calledFrom === 'minicart' ||
    currentPath?.includes?.(pageNames.viewCart) ||
    currentPath?.includes?.(pageNames.checkout) ||
    currentPath?.includes?.(pageNames.expressCheckout) ||
    currentPath?.includes?.(pageNames.orderConfirmation)
  ) {
    const pitchId = getPersonalOfferPitchId()
    const personalOfferUrl = pitchId ? `?pitchId=${pitchId}` : '/'
    return personalOfferUrl
  } else if (!hasMysite) {
    if (IS_BROWSER) {
      window?.location?.reload()
    }
  } else if (currentPath?.includes?.('/personal-offer')) {
    const sponsorId = getMysiteSponsor() || getPoSponsorId()
    const localeForUrl =
      getLocaleCodeFromUrl
        ?.toLowerCase?.()
        ?.split?.('_')
        ?.reverse?.()
        ?.join?.('/') || 'us/en'
    const mysiteUrl = `/${localeForUrl}/mysite/${sponsorId}`
    replaceLocation(mysiteUrl)
  }
}

function setSessionStorageData(sponserID, siteType) {
  setShopingContext(siteType)
  setSessionStorage(CONST_IS_EQUINOX_MYSITE_OR_POSITE, true)
  setSessionStorage(SPONSOR, sponserID)
  setMysiteOrPersonalOffer()
}

/**
 * Gets subscription attribution data for retail customers
 * Returns mysiteSponsorId and source based on whether request is from MYSITE or POSITE
 * @returns {Object} Object containing mysiteSponsorId and source properties
 */
function getSubscriptionAttributionData() {
  let mysiteSponsorId = ''
  let source = ''

  try {
    if (IS_BROWSER) {
      const contextValue = checkMysiteOrPersonalOffer()

      switch (contextValue) {
        case 'storefront':
          source = 'MYSITE'
          mysiteSponsorId =
            mysiteContainer?.mysiteSponsor ||
            getMysiteSponsor() ||
            getSessionStorage(SPONSOR) ||
            ''
          break
        case 'personal_offer':
          source = 'POSITE'
          mysiteSponsorId =
            personalOfferContainer?.poSiteSponsor ||
            getPoSponsorId() ||
            getSessionStorage(SPONSOR) ||
            ''
          break
        default:
          break
      }
    }
  } catch (error) {
    console.error('Error in getSubscriptionAttributionData:', error)
  }

  return {
    mysiteSponsorId,
    source,
  }
}

function getRetailSubscriptionAttributionData() {
  const accountType =
    customerContainer?.profileResponse?.accounts?.accountType ||
    getLocalStorage('accountType') ||
    ''

  const normalizedAccountType = (accountType || '')
    .replaceAll(REGEX_WHITESPACE, '')
    .toLowerCase()

  if (normalizedAccountType !== 'retailcustomer') {
    return null
  }

  const attributionData = getSubscriptionAttributionData()
  const mysiteSponsorId = attributionData?.mysiteSponsorId || ''

  if (!mysiteSponsorId) {
    return null
  }

  return {
    mysiteSponsorId,
    source: attributionData?.source || '',
  }
}

export {
  checkValidMySiteUrl,
  fetchAndValidateSubdomain,
  getMySiteUrlStatus,
  getMysiteShopHeader,
  getSponsorLookup,
  getMysiteContentVersion,
  isEquinoxMysite,
  isEquinoxPOSite,
  isPOSiteInvalid,
  deleteMysiteRelatedData,
  getSubdomainName,
  isEquinoxMysiteUrl,
  isSignupUrl,
  isMysiteHomePage,
  isPOHomePage,
  isMysiteOrPOHomePage,
  getSocialMediaType,
  getImageUrl,
  getBaseName,
  loadMySiteData,
  getMysiteSponsor,
  getMySiteTranslationContent,
  getPersonalOfferData,
  isEquinoxMysiteAdmin,
  isEquinoxMysiteAdminPreview,
  checkBASponsorIdMismatch,
  clearMysiteSessionStorage,
  getPoSponsorId,
  getPersonalOfferPitchId,
  getPersonalOfferRedirectUrl,
  redirectToLocaleHome,
  redirectToSignIn,
  redirectToPOLanding,
  getProductPriceForTracking,
  findDefaultSku,
  buildPersonalOfferUrl,
  loadRequiredAPIforPO,
  setSessionStorageData,
  getSubscriptionAttributionData,
  getRetailSubscriptionAttributionData,
  validateAndRedirectPOLocale,
  isEquinoxPOAdmin,
}
export default checkValidMySiteUrl
